import { ProjectAPI } from '@business/api/project';
import { UserId } from '@business/entities/account';
import { ContactId } from '@business/entities/contact';
import { OrgId, PaymentId } from '@business/entities/organization';
import { CompactProjectResponse, ProjectId, ProjectRequest, ProjectResponse, ProjectUpdateRequest } from '@business/entities/project';
import { ProjectStepResponse } from '@business/entities/project/ProjectSectionResponse';
import { ProjectTemplateId } from '@business/entities/project/TemplateResponse';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { gcLabel } from '@core/utils/hooks/misc/gcLabel';
import { resolve } from '@core/utils/ioc';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation, showConfirmation } from '@modules/common';
import { map, switchMap, tap } from 'rxjs/operators';
import { Blocs } from '../tokens';
import { SharedProjectAPI } from '@business/api/shared-project';
import { CompactSharedProjectResponse, SharedProjectResponse } from '@business/entities/sharedProject';
import { TenantId } from '@business/entities/tenant';
import { TenantIdentity } from '@business/entities/common';
import { TaskPriority } from '@business/constants/task';

export class SharedProjectBloc extends BaseBloc {
  private readonly projectRepo = new Repository<CompactSharedProjectResponse | SharedProjectResponse>({
    getItemId: project => `${project.tenant}-${project.id}`,
  });

  readonly projects$ = this.projectRepo.items$;
  readonly selectProject = this.projectRepo.selectItem;

  readonly selectStep = (tenant: string, projectId: ProjectId, stepId: string) =>
    this.projectRepo.selectItem(`${tenant}-${projectId}`).pipe(
      map(project => {
        if (!project) return undefined;
        const allSteps = R.flatMap((project as SharedProjectResponse).sections || [], section => section.steps);
        return allSteps.find(s => s.id === stepId);
      }),
    );

  onReset() {
    this.projectRepo.reset();
  }

  fetchProjects = () => {
    return SharedProjectAPI.fetchProjects().pipe(this.projectRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_pros_error') }));
  };

  fetchProject = (identity: TenantIdentity, id: ProjectId) => {
    return SharedProjectAPI.fetchProject(identity, id).pipe(
      this.projectRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_pros_error') }),
    );
  };

  toggleStep = (identity: TenantIdentity, projectId: ProjectId, stepId: number, completeAll?: boolean) => {
    return SharedProjectAPI.toggleStep(identity, stepId, completeAll).pipe(this.projectRepo.ops.upsertOne(item => ({ item })));
  };

  /* Sections */
  createSection = (identity: TenantIdentity, projectId: ProjectId, name: string) => {
    return SharedProjectAPI.createSection(identity, projectId, name).pipe(
      this.projectRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_template_section'),
      }),
    );
  };

  updateSection = (identity: TenantIdentity, projectId: ProjectId, sectionId: string, name: string) => {
    return SharedProjectAPI.updateSection(identity, sectionId, name).pipe(
      this.projectRepo.ops.upsertOne(item => ({ item })),

      handleMessage({
        type: requestMessage('update_template_section'),
      }),
    );
  };

  deleteSection = (identity: TenantIdentity, projectId: ProjectId, id: string) => {
    return showDeleteConfirmation('Delete Section', 'Do you really want to remove this Section?').pipe(
      switchMap(() => SharedProjectAPI.deleteSection(identity, id)),
      this.projectRepo.ops.upsertOne(item => ({ item })),

      handleMessage({
        type: requestMessage('delete_template_section'),
      }),
    );
  };

  duplicateSection = (identity: TenantIdentity, projectId: ProjectId, id: string) => {
    return showConfirmation('Duplicate Section', 'Do you really want to duplicate this Section?').pipe(
      switchMap(() => SharedProjectAPI.duplicateSection(identity, id)),
      this.projectRepo.ops.upsertOne(item => ({ item })),

      handleMessage({
        error: requestMessage('fetch_general_error'),
      }),
    );
  };

  //   /* Steps  */

  fetchStep = (identity: TenantIdentity, projectId: ProjectId, id: string) => {
    return SharedProjectAPI.fetchStep(identity, id).pipe(
      //   this.projectRepo.ops.updateOne(sections => ({
      //     id: projectId,
      //     changes: () => ({ sections }),
      //   })),
      handleMessage({
        error: requestMessage('fetch_step'),
      }),
    );
  };

  createStep = (identity: TenantIdentity, projectId: ProjectId, sectionId: string, data: Partial<ProjectStepResponse>, parentStepId?: string) => {
    return SharedProjectAPI.createStep(identity, sectionId, data, parentStepId).pipe(
      this.projectRepo.ops.upsertOne(item => ({ item })),

      handleMessage({
        type: requestMessage('create_template_section_step'),
      }),
    );
  };

  duplicateStep = (identity: TenantIdentity, projectId: ProjectId, id: string) => {
    return SharedProjectAPI.duplicateStep(identity, id).pipe(
      this.projectRepo.ops.upsertOne(item => ({ item })),

      handleMessage({
        error: requestMessage('fetch_general_error'),
      }),
    );
  };

  updateStep = (identity: TenantIdentity, projectId: ProjectId, id: string, data: Partial<ProjectStepResponse>) => {
    return SharedProjectAPI.updateStep(identity, id, data).pipe(
      this.projectRepo.ops.upsertOne(item => ({ item })),

      handleMessage({
        type: requestMessage('update_template_section_step'),
      }),
    );
  };

  deleteStep = (identity: TenantIdentity, projectId: ProjectId, id: string) => {
    return showDeleteConfirmation('Delete Step', 'Do you really want to remove this Step?').pipe(
      switchMap(() => SharedProjectAPI.deleteStep(identity, id)),
      this.projectRepo.ops.upsertOne(item => ({ item })),

      handleMessage({
        type: requestMessage('delete_template_section_step'),
      }),
    );
  };

  sortSectionSteps = (identity: TenantIdentity, projectId: ProjectId, sectionId: string, stepIds: string[], parentStepId?: string) => {
    return SharedProjectAPI.sortSectionSteps(identity, stepIds).pipe(
      tap(() => {
        const project = this.projectRepo.getItem(`${identity.tenant}-${projectId}`)! as SharedProjectResponse;

        const affectedSection = R.find(project.sections, s => s.id === sectionId)!;
        const newSteps = affectedSection.steps.map(step => {
          if (parentStepId) {
            const newSubSteps = step.subSteps.map(subStep => ({ ...subStep, menuOrder: R.findIndex(stepIds, i => i === subStep.id) }));
            return { ...step, subSteps: R.sortBy(newSubSteps, s => s.menuOrder) };
          }
          return { ...step, menuOrder: R.findIndex(stepIds, i => i === step.id) };
        });

        const newSections = project.sections.map(sec => {
          if (sec.id === sectionId) {
            return { ...sec, steps: R.sortBy(newSteps, s => s.menuOrder) };
          }
          return sec;
        });

        this.projectRepo.updateOne({
          id: `${identity.tenant}-${projectId}`,
          changes: () => ({ sections: newSections }),
        });
      }),
      handleMessage({
        error: requestMessage('update_template_steps_sort'),
      }),
    );
  };

  setProjectStepPriority = (identity: TenantIdentity, priority: TaskPriority, stepId: string, projectId: ProjectId) => {
    return SharedProjectAPI.setProjectStepPriority(identity, stepId, priority).pipe(
      this.projectRepo.ops.updateOne(sections => ({
        id: `${identity.tenant}-${projectId}`,
        changes: () => ({ sections }),
      })),
      handleMessage({
        error: requestMessage('project_step_priority'),
      }),
    );
  };

  orderTodos = (identity: TenantIdentity, projectId: ProjectId, items: any[]) => {
    return SharedProjectAPI.orderTodos(identity, projectId, items).pipe(this.projectRepo.ops.upsertOne(item => ({ item })));
  };

  assignProjectStep = (identity: TenantIdentity, projectId: ProjectId, stepId: string, data: string[]) => {
    return SharedProjectAPI.assignProjectStep(identity, stepId, data).pipe(
      this.projectRepo.ops.updateOne(sections => ({
        id: `${identity.tenant}-${projectId}`,
        changes: () => ({ sections }),
      })),
      handleMessage({
        error: requestMessage('update_template_steps_sort'),
      }),
    );
  };

  updateSectionClose = (identity: TenantIdentity, projectId: ProjectId, sectionId: string, isClosed: boolean) => {
    return SharedProjectAPI.updateSectionClose(identity, sectionId, isClosed).pipe(
      tap(() => {
        const project = this.projectRepo.getItem(`${identity.tenant}-${projectId}`)! as SharedProjectResponse;
        const newSections = project.sections.map(sec => {
          if (sec.id === sectionId) {
            return { ...sec, isClosed };
          }
          return sec;
        });

        this.projectRepo.updateOne({
          id: `${identity.tenant}-${projectId}`,
          changes: () => ({ sections: newSections }),
        });
      }),
    );
  };
}
